import { useLoaderData } from 'react-router';
import { useAtom } from 'jotai';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import useVariantPrice from '@modules/product/hook/use-variant-price';
import { buildPriceRangeString } from '@modules/product/component/variant-pricing/VariantPricing';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useSampleStock } from '@modules/product/hook/use-sample-stock';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    DetailSummaryRow,
    DetailSaveButton,
    TrackedWebsiteCta,
    detailCta,
} from '~/ui-rocco/pages/detail-shared';
import { PRODUCT_DETAILS_ANCHOR_ID } from './spec-accordion/ProductDetailsRowContent';
import { purchaseModalOpenAtom } from '../state/purchase-modal-atom';
import { useProductEnquiryModal } from '../hooks/use-product-enquiry-modal';
import { MobilePurchaseFlow } from './purchase/MobilePurchaseFlow';
import { DesktopPurchaseDrawer } from './purchase/DesktopPurchaseDrawer';
import { DesktopIslandEnquiryFallback } from './purchase/DesktopIslandEnquiryFallback';
import { ProductSampleLine } from './purchase/ProductShopBanner';
import {
    useSizesInUse,
    useVariantEnquiryContext,
} from './purchase/purchase-flow-parts';
import { resolveCtaState } from './purchase/cta-state';

/**
 * F4 / F6 — price + state-driven CTA + ecom modal.
 *
 * Right-aligned price block above the desktop CTA cluster. The primary slot
 * follows what the product can actually do: Shop Now when it is buyable, Shop
 * Sample when only a sample is, and otherwise the professional's configured
 * enquiry label (e.g. "Request Price / Brochure" — the same label the legacy
 * page shows). Visit Website + Save sit beside it.
 *
 * Commerce leading doesn't cost the page its enquiry: on those states the
 * professional's label keeps the outlined slot straight after Shop Now, so the
 * QDD action stays on the page rather than living only behind the drawer.
 *
 * Every commerce CTA opens the right slide-in drawer (`DesktopPurchaseDrawer`)
 * rather than transacting from the page — variant selector, stock/shipping and
 * a footer led by "Enquire about this configuration" with commerce (quantity +
 * add-to-cart / sample purchase) as the secondary, so the shopper configures
 * before they transact. Buyable-and-sampleable products get a quiet "Samples
 * available to order on ArchiPro" line under the cluster so the sample path
 * stays visible; every other state would just repeat its primary.
 *
 * Enquiry-led products open the drawer too whenever there is anything to pick
 * (the drawer folds the chosen variant into the enquiry), matching the meta
 * row's variant chips; with nothing to configure they skip straight to the
 * modal.
 *
 * Mobile is unchanged — it keeps Enquire as the primary with the shop banner
 * below (see `MobilePurchaseFlow`).
 *
 * Requires `VariantSelectionProvider` + `VariantOptionProvider` above it.
 */
export const ProductPurchaseSection = () => {
    const { product, primaryCTA } = useLoaderData<typeof loader>();
    const [open, setOpen] = useAtom(purchaseModalOpenAtom);

    const pricingType = product.Pricing.PricingType;
    const { price, maxPrice, isPriceRange, isOnSale, retailPrice } =
        useVariantPrice(pricingType);
    const { selectedVariant } = useVariantSelectionState();
    const { canPurchase: sampleCanPurchase } = useSampleStock(selectedVariant);

    const showMaxPrice =
        isPriceRange &&
        maxPrice.Amount > 0 &&
        price.Amount > 0 &&
        maxPrice.Amount !== price.Amount;
    const priceToShow = isPriceRange
        ? price.Amount > 0
            ? price
            : maxPrice
        : price;

    // Who is allowed to see a price at all, matching legacy `showPricing`
    // (Variants.tsx): a professional can switch pricing off for their whole
    // catalogue, and trade / specialty pricing is gated per user — a guest on a
    // trade-only variant gets `CanViewPrice: false` while `Price` still carries
    // the raw amount. Rendering from the amount alone published prices those
    // suppliers had opted out of.
    //
    // `priceString` is the single source for the page block, the drawer footer,
    // the mobile block and the island, so gating it here hides the price on
    // every surface at once.
    const canShowPrice =
        !product.Professional.PricingDisabled &&
        Boolean(selectedVariant?.CanViewPrice);
    const priceString =
        canShowPrice && priceToShow.Amount > 0
            ? buildPriceRangeString(
                  priceToShow.ShortFormat,
                  maxPrice.ShortFormat,
                  showMaxPrice,
              )
            : null;

    // A4 — struck-out retail price: only when sale and not a price range, and
    // only where a price may be shown at all.
    const retailPriceString =
        canShowPrice && isOnSale && !isPriceRange && retailPrice.Amount > 0
            ? retailPrice.ShortFormat.replace(/\.00$/, '')
            : null;

    // Price-range prefix, matching legacy VariantPricing: "From" when the range
    // has a lower bound, "Up to" when only a max price is set.
    const pricePrefix = isPriceRange
        ? price.Amount > 0
            ? 'From'
            : 'Up to'
        : null;

    // Mirror the legacy Variants buyable/sample detection so the CTA reflects
    // the real commerce state rather than the professional enquiry resolver.
    const hasVariantShippingOptions =
        (selectedVariant?.ShippingProfile?.ShippingMethods ?? []).length > 0 ||
        product.Professional.PickupLocations.length > 0;
    const hasSampleShippingMethods = selectedVariant?.SampleShippingProfile
        ?.NoShipping
        ? false
        : (selectedVariant?.SampleShippingProfile?.ShippingMethods ?? [])
              .length > 0;
    const hasSampleShippingOptions =
        hasSampleShippingMethods ||
        product.Professional.PickupLocations.length > 0;

    const isMarketplace = pricingType === 'ecommercePricing';
    const isBuyable =
        product.EcommerceReady &&
        isMarketplace &&
        hasVariantShippingOptions &&
        Boolean(selectedVariant?.CanPurchase);
    // A3: mirror legacy — sample purchase is independent of EcommerceReady
    // (legacy Variants.tsx:164 checks only sampleCanPurchase + hasSampleShippingOptions).
    const isSampleBuyable = sampleCanPurchase && hasSampleShippingOptions;

    // Does the product have anything to configure in the drawer?
    const { attributeMap } = useVariantOptionState();
    const sizesInUse = useSizesInUse();
    const hasVariantOptions = attributeMap.size > 0 || sizesInUse.length > 0;

    // Enquiry CTAs are gated on the professional's enquiry audience. Commerce
    // states lead with Shop Now / Shop Sample either way and reach the enquiry
    // through the drawer footer, so the gate only silences the enquiry-led
    // primary.
    const { showEnquiryCTA, showSpecifierLoginPrompt } = useEnquiryVisibility(
        product.Professional,
    );

    // A1: use the professional's configured CTA label for enquiry-led state
    // (e.g. "Request Price / Brochure") rather than generic "Enquire" /
    // "Request Quote". Falls back to the legacy defaults when absent.
    const {
        label: ctaLabel,
        isEnquiryCTA,
        opensEnquiryModalDirectly,
        showPrimaryCta,
        showSampleLine,
        showSecondaryEnquiry,
    } = resolveCtaState({
        isBuyable,
        isSampleBuyable,
        isMarketplace,
        hasVariantOptions,
        enquiryCTALabel: primaryCTA.primaryCTALabelTitleCase,
        enquiryVisible: showEnquiryCTA,
    });

    // The enquiry label always resolves to the professional's configured
    // label (or a generic fallback) — distinct from `ctaLabel`, which becomes
    // "Shop Now" on buyable products. Mobile shows Enquire on every product,
    // so it needs the enquiry label independently of the commerce state. The
    // supplier name is appended ("Get in Touch with Nero Tapware") so the CTA
    // names the counterparty — but only while the combined label still fits
    // the pill on one line. A long label ("Request Price / Brochure") or a
    // long supplier name would overflow, so those keep the bare label (the
    // trust row names the supplier right below the CTA anyway).
    //
    // The suffix earns its place where enquiry is the primary action (mobile,
    // the island, the modal header). Beside Shop Now it does not: it made the
    // outlined secondary 2.4x wider than the filled primary, so the desktop
    // CTA row uses `baseEnquiryLabel` and lets the fill carry the hierarchy.
    const baseEnquiryLabel =
        primaryCTA.primaryCTALabelTitleCase ||
        (isMarketplace ? 'Enquire' : 'Request Quote');
    const supplierName = product.Professional?.Title ?? '';
    const labelWithSupplier = `${baseEnquiryLabel} with ${supplierName}`;
    const enquiryLabel =
        supplierName &&
        !baseEnquiryLabel.includes(supplierName) &&
        labelWithSupplier.length <= 34
            ? labelWithSupplier
            : baseEnquiryLabel;

    const { openEnquiryModal } = useProductEnquiryModal();
    const onPrimaryCta = () => {
        if (opensEnquiryModalDirectly) {
            openEnquiryModal({ title: ctaLabel, ctaLabel });
            return;
        }
        setOpen(true);
    };

    // The magic island's enquiry pill — the page's enquiry entry point once the
    // CTA cluster has scrolled away. Opens the modal directly with the current
    // selection folded into the subject, same as the mobile page CTA.
    const variantEnquiry = useVariantEnquiryContext();
    const onEnquireClick = () =>
        openEnquiryModal({
            title: enquiryLabel,
            ctaLabel: enquiryLabel,
            messageSubject: variantEnquiry.messageSubject,
            itemImage: variantEnquiry.itemImage,
        });

    // A2: "Request a Sample" enquiry — shown when the product has samples
    // available (non-buyable samples) and the enquiry CTA is active.
    const showRequestSampleEnquiry =
        Boolean(product.SamplesAvailable) && isEnquiryCTA && showEnquiryCTA;
    const onRequestSampleEnquiry = () => {
        openEnquiryModal({
            title: 'Request a Sample',
            ctaLabel: 'Request a Sample',
            messageSubject: `a sample of ${product.Title}`,
        });
    };

    const { mobile } = useAppDisplayModeContext();
    if (mobile) {
        // BC-4524 — mobile runs the whole purchase path inline (no modal).
        return (
            <MobilePurchaseFlow
                priceString={priceString}
                isPriceRange={isPriceRange}
                isOnSale={isOnSale}
                retailPriceString={retailPriceString}
                pricePrefix={pricePrefix}
                isBuyable={isBuyable}
                isSampleBuyable={isSampleBuyable}
                enquiryLabel={enquiryLabel}
                showCTA={showEnquiryCTA}
                showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                showRequestSampleEnquiry={showRequestSampleEnquiry}
                onRequestSampleEnquiry={onRequestSampleEnquiry}
            />
        );
    }

    return (
        <>
            {/* Desktop island fallback: price + Enquire from 30% scroll depth,
                whenever no section claims the island. */}
            <DesktopIslandEnquiryFallback
                priceString={priceString}
                enquiryLabel={enquiryLabel}
                show={showEnquiryCTA}
                onEnquire={onEnquireClick}
            />
            <DetailSummaryRow
                caption={`Introducing ${product.Title}`}
                descriptionHtml={product.Content ?? undefined}
                clampLines={3}
                readMoreAnchor={`#${PRODUCT_DETAILS_ANCHOR_ID}`}
                aside={
                    <>
                        {priceString ? (
                            // Tight internal spacing — the aside column's own
                            // gap (1.5rem) is too wide between the price lines.
                            <div className="flex flex-col items-end gap-1">
                                {isOnSale &&
                                !isPriceRange &&
                                retailPriceString ? (
                                    <del className="m-0 block font-sans text-base font-normal text-[#767676]">
                                        {retailPriceString}
                                    </del>
                                ) : null}
                                <p className="m-0 font-sans leading-[0.86] font-medium tracking-[-0.05em] text-[#181818] max-md:text-[2.25rem] md:text-[3.125rem]">
                                    {pricePrefix ? (
                                        <span className="mr-2 align-middle text-[0.4em] font-normal tracking-normal">
                                            {pricePrefix}
                                        </span>
                                    ) : null}
                                    {priceString}
                                    {isPriceRange ? (
                                        <span className="align-super text-[0.45em] text-[#767676]">
                                            *
                                        </span>
                                    ) : null}
                                </p>
                                {isPriceRange ? (
                                    <p className="m-0 font-sans text-sm text-[#767676]">
                                        *Price indication
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                        {!product.IsInServiceableArea ? (
                            <p className="m-0 font-sans text-base font-normal text-[#6b6b6b]">
                                Not available in your location.
                            </p>
                        ) : null}
                        {/* Wraps to the right (the aside column is items-end)
                            so a long enquiry label beside Shop Now never
                            overflows the column at narrower desktop widths. */}
                        <div className="flex flex-wrap items-center justify-end gap-2 max-md:w-full max-md:flex-col">
                            {/* One primary, matching what the product can do:
                                Shop Now / Shop Sample into the drawer, or the
                                professional's enquiry label. */}
                            {showPrimaryCta ? (
                                <button
                                    type="button"
                                    onClick={onPrimaryCta}
                                    className={detailCta(
                                        'filled-light',
                                        'max-md:w-full',
                                    )}
                                >
                                    {ctaLabel}
                                </button>
                            ) : null}
                            {/* Commerce leads, but enquiry is still the page's
                                highest-value action — it keeps the slot right
                                after Shop Now rather than living only in the
                                drawer. Bare label (no supplier suffix) so the
                                secondary stays narrower than the primary; the
                                modal it opens still names the supplier. */}
                            {showSecondaryEnquiry ? (
                                <button
                                    type="button"
                                    onClick={onEnquireClick}
                                    className={detailCta(
                                        'outlined-light',
                                        'max-md:w-full',
                                    )}
                                >
                                    {baseEnquiryLabel}
                                </button>
                            ) : null}
                            {/* A2: "Request a Sample" enquiry button */}
                            {showRequestSampleEnquiry ? (
                                <button
                                    type="button"
                                    onClick={onRequestSampleEnquiry}
                                    className={detailCta(
                                        'outlined-light',
                                        'max-md:w-full',
                                    )}
                                >
                                    Request a Sample
                                </button>
                            ) : null}
                            <TrackedWebsiteCta
                                website={
                                    product.Website ||
                                    product.Professional.Website
                                }
                                libraryItemID={product.ID}
                                professionalID={product.Professional.ID}
                                professionalTitle={product.Professional.Title}
                                variant="outlined-light"
                                className="max-md:w-full"
                            />

                            <DetailSaveButton
                                pinId={product.ID}
                                pinType="ProductPin"
                            />
                        </div>
                        {/* Shop Now owns the primary slot on a product that can
                            also be sampled — this quiet line keeps the sample
                            path visible, opening the same drawer. */}
                        {showSampleLine ? (
                            <ProductSampleLine onOpen={() => setOpen(true)} />
                        ) : null}
                        {isEnquiryCTA &&
                        !showEnquiryCTA &&
                        showSpecifierLoginPrompt ? (
                            <SpecifierLoginPrompt
                                professionalName={product.Professional.Title}
                                showBottomBorder={false}
                                className="mt-4"
                            />
                        ) : null}
                    </>
                }
            />

            <DesktopPurchaseDrawer
                open={open}
                onOpenChange={setOpen}
                isBuyable={isBuyable}
                isSampleBuyable={isSampleBuyable}
                priceString={priceString}
                isPriceRange={isPriceRange}
                isOnSale={isOnSale}
                retailPriceString={retailPriceString}
                pricePrefix={pricePrefix}
                enquiryLabel={enquiryLabel}
                showCTA={showEnquiryCTA}
                showSpecifierLoginPrompt={showSpecifierLoginPrompt}
            />
        </>
    );
};
